import { useState, useEffect, useCallback } from 'react';
import { notificationsApi } from './notificationsApi';
import { NotificationItem } from './types';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await notificationsApi.getNotifications();

      if (response.status === 'Success' && response.data) {
        const { notifications: notificationList, unreadCount: count } = response.data;

        // Map API response to expected frontend structure
        const mappedNotifications = notificationList.map((notification: any) => ({
          id: notification._id || notification.id,
          title: notification.title || notification.subject || 'Notification',
          message: notification.message || notification.content || notification.body || '',
          type: notification.type || 'info',
          isRead: notification.isRead || notification.read || false,
          createdAt: notification.createdAt || notification.created_at || new Date().toISOString(),
          updatedAt: notification.updatedAt || notification.updated_at || new Date().toISOString(),
          // Include navigation-related fields for admin
          ticketId: notification.ticketId || notification.ticket_id || notification.relatedTicket ||
                   (notification.relatedType === 'support_ticket' || notification.relatedType === 'ticket' ?
                    notification.relatedId : null),
          relatedTicketId: notification.relatedTicketId || notification.related_ticket_id,
          userId: notification.userId || notification.user_id || notification.customerId ||
                 (notification.relatedType === 'user' || notification.relatedType === 'customer' ?
                  notification.relatedId : null),
          relatedUserId: notification.relatedUserId || notification.related_user_id,
          orderId: notification.orderId || notification.order_id || notification.giftId ||
                  (notification.relatedType === 'order' || notification.relatedType === 'gift' ?
                   notification.relatedId : null),
          relatedOrderId: notification.relatedOrderId || notification.related_order_id,
          // Store the original relatedId and relatedType
          relatedId: notification.relatedId,
          relatedType: notification.relatedType,
        }));

        // Sort notifications by creation date (newest first)
        const sortedNotifications = mappedNotifications.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setNotifications(sortedNotifications);
        setUnreadCount(count);
      } else {
        setError(response.message || 'Failed to fetch notifications');
      }
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed');
      } else if (error.response?.status === 404) {
        // If endpoint doesn't exist yet, just set empty state
        setNotifications([]);
        setUnreadCount(0);
      } else {
        setError('Failed to load notifications');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark a single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const response = await notificationsApi.markAsRead(notificationId);

      if (response.status === 'Success') {
        // Update local state
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === notificationId
              ? { ...notification, isRead: true }
              : notification
          )
        );

        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await notificationsApi.markAllAsRead();

      if (response.status === 'Success') {
        // Update local state
        setNotifications(prev =>
          prev.map(notification => ({ ...notification, isRead: true }))
        );

        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const response = await notificationsApi.deleteNotification(notificationId);

      if (response.status === 'Success') {
        // Remove from local state
        setNotifications(prev =>
          prev.filter(notification => notification.id !== notificationId)
        );

        // Update unread count if the deleted notification was unread
        const deletedNotification = notifications.find(n => n.id === notificationId);
        if (deletedNotification && !deletedNotification.isRead) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [notifications]);

  // Refresh notifications
  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // Poll for new notifications every 2 minutes
  useEffect(() => {
    // Initial fetch
    fetchNotifications();

    // Set up polling interval
    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(intervalId);
  }, [fetchNotifications]);

  // Listen for focus events to refresh notifications when user returns to tab
  useEffect(() => {
    const handleFocus = () => {
      fetchNotifications();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    fetchNotifications
  };
};